import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "@/lib/router-compat";
import { useQueryClient } from "@tanstack/react-query";
import { User, Buildings as Building2, Phone, MapPin, FileText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { SEOHead } from "@/components/seo/SEOHead";
import { SEOHreflang } from "@/components/seo/SEOHreflang";
import { useCanonicalUrl } from "@/hooks/useCanonicalUrl";
import { useTranslationV2 } from "@/hooks/useTranslationV2";
import { useCountry } from "@/hooks/useCountry";
import { COUNTRY_ROUTES } from "@/utils/countryRoutes";
import { useCountryLink } from "@/hooks/useCountryLink";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { Envelope as Mail, Lock, Eye, EyeSlash as EyeOff, CircleNotch as Loader2 } from "@phosphor-icons/react";
import { trackViewContent, trackInitiateCheckout, generateEventId } from "@/utils/metaPixel";
import PaymentMethodsBadge from "@/components/listings/detail/PaymentMethodsBadge";
import { PlansOverview } from "@/components/pricing/PlansOverview";
import { PricingSchema } from "@/components/pricing/PricingSchema";

const getAuthLabels = (country: 'RO' | 'UK' | 'DE') => {
  if (country === 'UK') {
    return {
      loginTab: 'Sign in',
      signupTab: 'Create account',
      emailLabel: 'Email',
      emailPlaceholder: 'Enter your Email',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your Password',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot password?',
      signInButton: 'Sign In',
      signUpButton: 'Sign Up',
      loading: 'Loading...',
      noAccount: "Don't have an account? ",
      hasAccount: "Already have an account? ",
      noConfirmationEmail: "Didn't receive confirmation email?",
      resendEmail: 'Resend confirmation email',
      loginFailed: 'Login failed',
      signupFailed: 'Signup failed',
      accountExists: 'Account exists',
      accountExistsDesc: 'This email is already registered. Try logging in or reset your password.',
      emailRejected: 'Email rejected by server',
      verifyEmail: 'Verify email',
      verifyEmailDesc: "We've sent you a confirmation email. Check your inbox and spam folder.",
      accountCreated: 'Account created successfully',
      welcome: 'Welcome to RO Auto Market!',
      emailRequired: 'Email required',
      emailRequiredDesc: 'Enter your email to reset password.',
      emailSent: 'Email sent',
      emailSentDesc: 'Check your email for the password reset link.',
      resetFailed: 'Reset failed',
      invalidEmailFormat: 'Possible invalid email format',
      invalidEmailDesc: 'We\'ll try anyway. Check for spaces or invisible characters.',
      authTitle: 'Sign in or create account',
      // Consent labels
      consentIntro: 'By clicking Sign Up, I accept the',
      termsOfUse: 'Terms of Use',
      consentDataProcessing: 'I understand that ROAutoMarket.ro uses my personal data in accordance with the',
      privacyPolicy: 'Privacy Policy',
      andThe: 'and the',
      cookiePolicy: 'Cookie Policy',
      consentDataUsage: 'ROAutoMarket.ro uses automated systems and partners to analyse how I use the services, to provide relevant product functionality and content, as well as protection against spam, malware and unauthorised use.',
      consentMarketing: 'I agree to receive commercial communications (e.g. newsletters, updates) about ROAutoMarket.ro and its partners.',
    };
  }
  if (country === 'DE') {
    return {
      loginTab: 'Anmelden',
      signupTab: 'Konto erstellen',
      emailLabel: 'E-Mail',
      emailPlaceholder: 'Geben Sie Ihre E-Mail ein',
      passwordLabel: 'Passwort',
      passwordPlaceholder: 'Geben Sie Ihr Passwort ein',
      rememberMe: 'Angemeldet bleiben',
      forgotPassword: 'Passwort vergessen?',
      signInButton: 'Anmelden',
      signUpButton: 'Registrieren',
      loading: 'Wird geladen...',
      noAccount: 'Sie haben noch kein Konto? ',
      hasAccount: 'Sie haben bereits ein Konto? ',
      noConfirmationEmail: 'Bestätigungs-E-Mail nicht erhalten?',
      resendEmail: 'Bestätigungs-E-Mail erneut senden',
      loginFailed: 'Anmeldung fehlgeschlagen',
      signupFailed: 'Registrierung fehlgeschlagen',
      accountExists: 'Konto existiert',
      accountExistsDesc: 'Diese E-Mail ist bereits registriert. Versuchen Sie sich anzumelden oder setzen Sie Ihr Passwort zurück.',
      emailRejected: 'E-Mail vom Server abgelehnt',
      verifyEmail: 'E-Mail verifizieren',
      verifyEmailDesc: 'Wir haben Ihnen eine Bestätigungs-E-Mail gesendet. Überprüfen Sie Ihren Posteingang und Spam-Ordner.',
      accountCreated: 'Konto erfolgreich erstellt',
      welcome: 'Willkommen bei RO Auto Market!',
      emailRequired: 'E-Mail erforderlich',
      emailRequiredDesc: 'Geben Sie Ihre E-Mail ein, um das Passwort zurückzusetzen.',
      emailSent: 'E-Mail gesendet',
      emailSentDesc: 'Überprüfen Sie Ihre E-Mail für den Link zum Zurücksetzen des Passworts.',
      resetFailed: 'Zurücksetzen fehlgeschlagen',
      invalidEmailFormat: 'Möglicherweise ungültiges E-Mail-Format',
      invalidEmailDesc: 'Wir versuchen es trotzdem. Überprüfen Sie auf Leerzeichen oder unsichtbare Zeichen.',
      authTitle: 'Anmelden oder Konto erstellen',
      // Consent labels
      consentIntro: 'Mit dem Klick auf Registrieren akzeptiere ich die',
      termsOfUse: 'Nutzungsbedingungen',
      consentDataProcessing: 'Ich verstehe, dass ROAutoMarket.ro meine personenbezogenen Daten gemäß der',
      privacyPolicy: 'Datenschutzerklärung',
      andThe: 'und der',
      cookiePolicy: 'Cookie-Richtlinie',
      consentDataUsage: 'ROAutoMarket.ro verwendet automatisierte Systeme und Partner, um zu analysieren, wie ich die Dienste nutze, um relevante Produktfunktionen und Inhalte sowie Schutz vor Spam, Malware und unbefugter Nutzung bereitzustellen.',
      consentMarketing: 'Ich stimme dem Erhalt von kommerziellen Mitteilungen (z.B. Newsletter, Updates) über ROAutoMarket.ro und seine Partner zu.',
    };
  }
  return {
    loginTab: 'Intră în cont',
    signupTab: 'Creează cont',
    emailLabel: 'Email',
    emailPlaceholder: 'Introdu emailul tău',
    passwordLabel: 'Parolă',
    passwordPlaceholder: 'Introdu parola ta',
    rememberMe: 'Ține-mă minte',
    forgotPassword: 'Ai uitat parola?',
    signInButton: 'Intră în cont',
    signUpButton: 'Creează cont',
    loading: 'Se încarcă...',
    noAccount: 'Nu ai cont? ',
    hasAccount: 'Ai deja cont? ',
    noConfirmationEmail: 'Nu ai primit email-ul de confirmare?',
    resendEmail: 'Retrimite email-ul de confirmare',
    loginFailed: 'Autentificare eșuată',
    signupFailed: 'Înregistrare eșuată',
    accountExists: 'Cont existent',
    accountExistsDesc: 'Acest email este deja înregistrat. Încearcă să te autentifici sau resetează parola.',
    emailRejected: 'Email respins de server',
    verifyEmail: 'Verifică emailul',
    verifyEmailDesc: 'Ți-am trimis un email de confirmare. Verifică inbox-ul și spam-ul.',
    accountCreated: 'Cont creat cu succes',
    welcome: 'Bun venit pe RO Auto Market!',
    emailRequired: 'Email necesar',
    emailRequiredDesc: 'Introdu email-ul pentru resetarea parolei.',
    emailSent: 'Email trimis',
    emailSentDesc: 'Verifică emailul pentru link-ul de resetare parolă.',
    resetFailed: 'Resetare eșuată',
    invalidEmailFormat: 'Format email posibil invalid',
    invalidEmailDesc: 'Încercăm oricum. Verifică să nu existe spații sau caractere invizibile.',
    authTitle: 'Intră în cont sau creează unul nou',
    // Consent labels
    consentIntro: 'Prin clic pe butonul Creează cont, accept',
    termsOfUse: 'Termenii de utilizare',
    consentDataProcessing: 'Am înțeles că ROAutoMarket.ro îmi folosește datele personale în conformitate cu',
    privacyPolicy: 'Declarația de confidențialitate',
    andThe: 'și',
    cookiePolicy: 'Politica privind modulele cookie',
    consentDataUsage: 'ROAutoMarket.ro recurge la sisteme automate și la parteneri care analizează modul în care utilizez serviciile, pentru a oferi funcționalitate și conținut relevante, precum și protecție împotriva mesajelor nesolicitate, software-ului rău intenționat și utilizării neautorizate.',
    consentMarketing: 'Sunt de acord cu folosirea mijloacelor de comunicare electronică pentru trimiterea de informații de natură comercială (de ex. buletine informative, actualizări) privind ROAutoMarket.ro și partenerii săi.',
  };
};

const cleanupAuthState = () => {
  try {
    localStorage.removeItem('supabase.auth.token');
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        localStorage.removeItem(key);
      }
    });
    Object.keys(sessionStorage || {}).forEach((key) => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (error) {
    // Ignore cleanup errors
  }
};

export default function Pricing() {
  const price = 50;
  const location = useLocation();
  const [hadTrial, setHadTrial] = useState(false);
  const [hasListings, setHasListings] = useState(false); // Track if user has listings
  const canonicalUrl = useCanonicalUrl();
  const { t } = useTranslationV2('pricing');
  const { country } = useCountry();
  const { getLink } = useCountryLink();
  const authLabels = getAuthLabels(country);

  // Auth state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [accountType, setAccountType] = useState<"buyer" | "dealer">("buyer");
  const [companyName, setCompanyName] = useState("");
  const [cui, setCui] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  const [activeAuthTab, setActiveAuthTab] = useState<string>("signup"); // Controlled tab state
  const [authNotice, setAuthNotice] = useState<{
    variant?: "default" | "destructive";
    title: string;
    description?: string;
  } | null>(null);
  const [signupErrors, setSignupErrors] = useState<Record<string, string>>({});
  const [cardSetupPending, setCardSetupPending] = useState(false);
  const [hasCard, setHasCard] = useState<boolean | null>(null); // null = loading

  const clearSignupError = (field: string) => {
    setSignupErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const focusSignupError = (errs: Record<string, string>) => {
    const order = ['name', 'phone', 'email', 'password', 'companyName', 'cui'];
    const first = order.find((field) => errs[field]);
    if (!first) return;
    const idMap: Record<string, string> = {
      name: 'signup-name',
      phone: 'signup-phone',
      email: 'email2',
      password: 'password2',
      companyName: 'signup-company',
      cui: 'signup-cui',
    };
    const el = document.getElementById(idMap[first]);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => (el as HTMLInputElement).focus(), 250);
    }
  };

  const signupFieldLabels: Record<string, string> = {
    name: country === 'DE' ? 'Name' : country === 'UK' ? 'Name' : 'Nume',
    phone: country === 'DE' ? 'Telefon' : country === 'UK' ? 'Phone' : 'Telefon',
    email: authLabels.emailLabel,
    password: authLabels.passwordLabel,
    companyName: country === 'DE' ? 'Firmenname' : country === 'UK' ? 'Company name' : 'Nume companie',
    cui: country === 'DE' ? 'Steuernummer/CUI' : country === 'UK' ? 'Company/VAT number' : 'CUI',
  };

  const offerCatalogLd = {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    name: "Planuri publicare anunț",
    itemListElement: [
      {
        "@type": "Offer",
        name: "Standard",
        price: 50,
        priceCurrency: "EUR",
        description: "Abonament lunar cu plată imediată. Fără trial, fără comision la vânzare."
      },
      {
        "@type": "Offer", 
        name: "Standard",
        price: 50,
        priceCurrency: "EUR",
        description: "60 de anunțuri active + 1 anunț promovat, import CSV, cereri pe email"
      }
    ]
  };
  
  const priceId: string | undefined = undefined;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success') {
      toast({
        title: t('toast_success_title'),
        description: t('toast_success_paid'),
      });
      // Force refresh of role cache so a buyer who just paid is immediately
      // recognized as a dealer (trigger auto_grant_dealer_role_on_subscription
      // adds the dealer role on the subscribers row).
      queryClient.invalidateQueries({ queryKey: ['user-role'] });
    } else if (checkout === 'cancelled') {
      toast({
        title: t('toast_cancelled_title'),
        description: t('toast_cancelled_desc'),
        variant: "destructive",
      });
    }
  }, [location.search, hadTrial, t, queryClient]);

  useEffect(() => {
    const check = async () => {
      const { data } = await supabase.auth.getSession();
      const user = data.session?.user;
      setIsAuthed(!!user);
      if (user) {
        try {
          const { data: subscriber } = await supabase
            .from('subscribers')
            .select('subscription_end, stripe_customer_id')
            .eq('user_id', user.id)
            .maybeSingle();
          
          setHadTrial(!!(subscriber && subscriber.subscription_end !== null));
          setHasCard(!!(subscriber?.stripe_customer_id));
          
          // Check if user has any listings
          const { count } = await supabase
            .from('listings_v2')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id);
          
          setHasListings((count || 0) > 0);
          
          try { await supabase.functions.invoke('check-subscription'); } catch {}
          const { data: isSub } = await supabase.rpc('is_user_subscribed', { _uid: user.id });
          if (isSub) {
            toast({ title: t('toast_already_subscribed'), description: t('toast_already_desc') });
            navigate(COUNTRY_ROUTES.dashboard(country), { replace: true });
          }
        } catch {}
      }
    };
    const { data: sub } = supabase.auth.onAuthStateChange(() => { check(); });
    check();
    return () => sub.subscription.unsubscribe();
  }, [navigate, t, country]);

  // Meta Pixel: ViewContent when user lands on pricing
  useEffect(() => {
    trackViewContent('Pricing');
  }, []);

  // Auth handlers
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthNotice(null);
    setLoading(true);
    
    try {
      cleanupAuthState();
      await new Promise(resolve => setTimeout(resolve, 100));
      
      try {
        await supabase.auth.signOut({ scope: 'global' });
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch {}
      
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email: email.trim().toLowerCase(), 
        password 
      });
      
      if (error) throw error;
      
      if (data?.user) {
        const dashboardUrl = COUNTRY_ROUTES.dashboard(country);
        window.location.href = dashboardUrl;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : authLabels.loginFailed;
      setAuthNotice({ variant: "destructive", title: authLabels.loginFailed, description: msg });
      toast({ title: authLabels.loginFailed, description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthNotice(null);
    setSignupErrors({});
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const trimmedEmail = email.trim().toLowerCase();
    const normalizedEmail = trimmedEmail
      .normalize('NFKC')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/\s+/g, '');

    const errs: Record<string, string> = {};
    if (!normalizedEmail || !emailRegex.test(normalizedEmail)) {
      errs.email = authLabels.invalidEmailFormat;
    }
    if (!password || password.length < 8) {
      errs.password = country === 'UK' ? 'Minimum 8 characters' : country === 'DE' ? 'Mindestens 8 Zeichen' : 'Minim 8 caractere';
    }
    if (!name.trim()) errs.name = country === 'UK' ? 'Name is required' : country === 'DE' ? 'Name ist erforderlich' : 'Numele este obligatoriu';
    if (!phone.trim()) errs.phone = country === 'UK' ? 'Phone is required' : country === 'DE' ? 'Telefon ist erforderlich' : 'Telefonul este obligatoriu';
    if (accountType === "dealer") {
      if (!companyName.trim()) errs.companyName = country === 'UK' ? 'Company name is required' : country === 'DE' ? 'Firmenname ist erforderlich' : 'Numele companiei este obligatoriu';
      if (!cui.trim()) errs.cui = country === 'UK' ? 'Company/VAT number is required' : country === 'DE' ? 'Steuernummer/CUI ist erforderlich' : 'CUI-ul este obligatoriu';
    }

    if (Object.keys(errs).length) {
      const missing = Object.keys(errs).map((key) => signupFieldLabels[key] || key).join(', ');
      setSignupErrors(errs);
      setAuthNotice({
        variant: "destructive",
        title: country === 'UK' ? 'Complete the required fields' : country === 'DE' ? 'Bitte Pflichtfelder ausfüllen' : 'Completează câmpurile obligatorii',
        description: country === 'UK' ? `Missing/invalid: ${missing}` : country === 'DE' ? `Fehlt/ungültig: ${missing}` : `Lipsește/invalid: ${missing}`,
      });
      toast({
        title: country === 'UK' ? 'Complete the required fields' : country === 'DE' ? 'Bitte Pflichtfelder ausfüllen' : 'Completează câmpurile obligatorii',
        description: country === 'UK' ? `Missing/invalid: ${missing}` : country === 'DE' ? `Fehlt/ungültig: ${missing}` : `Lipsește/invalid: ${missing}`,
        variant: "destructive",
        duration: 8000,
      });
      focusSignupError(errs);
      return;
    }

    setLoading(true);
    try {
      const { data: preflightSession } = await supabase.auth.getSession();
      if (preflightSession.session) {
        await supabase.auth.signOut({ scope: 'global' });
        await new Promise(resolve => setTimeout(resolve, 150));
      }
    } catch {
      // Continue signup even if cleanup fails; Supabase will return a visible error if needed.
    }
    
    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;
      
      const { data, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: { 
          emailRedirectTo: redirectUrl,
          data: {
            country,
            full_name: name.trim(),
            phone: phone.trim(),
            account_type: accountType,
            ...(accountType === "dealer" && { company_name: companyName.trim(), cui: cui.trim(), address: address.trim() }),
            marketing_consent: marketingConsent,
          }
        },
      });
      
      if (error) {
        console.error('[Signup] Error:', error.message, 'Status:', (error as any).status, 'Code:', (error as any).code);
        
        const errCode = (error as any).code || '';
        const errMsg = error.message || '';
        const isWeakPassword = errCode === 'weak_password' || /weak.?password|pwned|compromised|breach/i.test(errMsg);
        const isUserExists = errCode === 'user_already_exists' || /already.*registered|user.*already|already.*exists/i.test(errMsg);
        const is422 = (error as any).status === 422;
        
        // 1. Parolă compromisă/slabă — VERIFICAT ÎNAINTE de "user exists"
        if (isWeakPassword) {
          setAuthNotice({
            variant: "destructive",
            title: "Parolă nesigură",
            description: "Această parolă a apărut într-o scurgere publică de date. Alege o parolă unică (minim 8 caractere, cu litere, cifre și simboluri).",
          });
          toast({
            title: "Parolă nesigură",
            description: "Parola ta a fost detectată într-o scurgere publică. Te rugăm alege alta — unică, cu litere, cifre și simboluri.",
            variant: "destructive",
            duration: 10000,
          });
          return;
        }
        
        // Only treat as "account exists" if the error message explicitly says so
        if (isUserExists) {
          // Auto-attempt login with the credentials the user just entered
          console.log('[Signup] Email already exists, attempting auto-login...');
          const { data: loginData, error: loginError } = await supabase.auth.signInWithPassword({
            email: normalizedEmail,
            password,
          });

          if (!loginError && loginData?.session) {
            console.log('[Signup] Auto-login successful');
            toast({ 
              title: authLabels.accountCreated || 'Autentificare reușită', 
              description: authLabels.welcome || 'Bine ai revenit!'
            });
            // Only redirect to card setup for dealers
            if (accountType === "dealer") {
              try {
                const { data: setupData, error: setupError } = await supabase.functions.invoke('setup-card');
                if (!setupError && setupData?.url) {
                  window.location.href = setupData.url;
                  return;
                }
              } catch (e) {
                console.error("Setup card error:", e);
              }
            }
            const dashboardUrl = COUNTRY_ROUTES.dashboard(country);
            window.location.href = dashboardUrl;
            return;
          }

          // Login failed - send password reset email automatically
          console.log('[Signup] Auto-login failed, sending password reset email...');
          const resetRedirectUrl = `${window.location.origin}/auth/callback`;
          await supabase.auth.resetPasswordForEmail(normalizedEmail, {
            redirectTo: resetRedirectUrl,
          });

          setActiveAuthTab("login");
          setAuthNotice({
            variant: "destructive",
            title: authLabels.accountExists,
            description: `${authLabels.accountExistsDesc} Ți-am trimis un email de resetare a parolei. Verifică inbox-ul (și spam).`,
          });
          toast({
            title: authLabels.accountExists,
            description: `Ți-am trimis automat un email de resetare a parolei la ${normalizedEmail}. Verifică inbox-ul.`,
            variant: "destructive",
            duration: 10000
          });
          return;
        }
        
        if (is422 && !isUserExists) {
          // 422 but NOT "already registered" - likely signups disabled or other config issue
          console.error('[Signup] 422 error but not user-exists:', error.message);
          setAuthNotice({
            variant: "destructive",
            title: "Înregistrare temporar indisponibilă",
            description: `Eroare la crearea contului: ${error.message}. Te rugăm contactează-ne pe WhatsApp sau încearcă mai târziu.`,
          });
          toast({
            title: "Înregistrare indisponibilă",
            description: error.message,
            variant: "destructive",
            duration: 10000
          });
          return;
        }
        
        if (error.message.includes('invalid') || error.message.includes('Invalid')) {
          setAuthNotice({
            variant: "destructive",
            title: authLabels.emailRejected,
            description: `${authLabels.emailRejected}. ${error.message}`,
          });
          toast({
            title: authLabels.emailRejected,
            description: `${authLabels.emailRejected}. ${error.message}`,
            variant: "destructive"
          });
          return;
        }
        
        throw error;
      }
      
      // Nota: Email-ul de bun venit se trimite automat prin database trigger (on_subscriber_created_send_welcome)
      if (data.user && !data.session) {
        setAuthNotice({
          variant: "default",
          title: authLabels.verifyEmail,
          description: authLabels.verifyEmailDesc,
        });
        toast({ 
          title: authLabels.verifyEmail, 
          description: authLabels.verifyEmailDesc,
          duration: 8000
        });
      } else if (data.session) {
        toast({ 
          title: authLabels.accountCreated, 
          description: authLabels.welcome
        });
        
        // Only dealers need card setup — buyers go straight to dashboard
        if (accountType === "buyer") {
          window.location.href = COUNTRY_ROUTES.dashboard(country);
          return;
        }
        
        // Dealer flow: Redirect to Stripe card setup
        let attempts = 0;
        const maxAttempts = 3;
        let stripeRedirectUrl: string | null = null;
        
        // Fire Meta InitiateCheckout pixel before redirect
        trackInitiateCheckout();
        
        while (attempts < maxAttempts && !stripeRedirectUrl) {
          attempts++;
          try {
            console.log(`[Signup] Calling setup-card (attempt ${attempts}/${maxAttempts})...`);
            const { data: setupData, error: setupError } = await supabase.functions.invoke('setup-card');
            console.log('[Signup] setup-card response:', { setupData, setupError });
            
            if (!setupError && setupData?.url) {
              stripeRedirectUrl = setupData.url;
            } else {
              console.error(`[Signup] setup-card attempt ${attempts} failed:`, setupError, setupData);
              if (attempts < maxAttempts) {
                await new Promise(r => setTimeout(r, 1000));
              }
            }
          } catch (e) {
            console.error(`[Signup] setup-card attempt ${attempts} exception:`, e);
            if (attempts < maxAttempts) {
              await new Promise(r => setTimeout(r, 1000));
            }
          }
        }
        
        if (stripeRedirectUrl) {
          console.log('[Signup] Redirecting to Stripe:', stripeRedirectUrl);
          window.location.href = stripeRedirectUrl;
          return;
        }
        
        // All attempts failed - show error with manual retry
        console.error('[Signup] All setup-card attempts failed!');
        setAuthNotice({
          variant: "destructive",
          title: "⚠️ Înregistrarea cardului nu a reușit",
          description: "Contul a fost creat, dar nu am putut deschide pagina de înregistrare a cardului. Apasă butonul de mai jos pentru a încerca din nou.",
        });
        toast({
          title: "⚠️ Card neînregistrat",
          description: "Te rugăm să încerci din nou. Contul nu este complet fără card.",
          variant: "destructive",
          duration: 15000,
        });
        // Store a flag so we can show a retry button
        setCardSetupPending(true);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : authLabels.signupFailed;
      setAuthNotice({ variant: "destructive", title: authLabels.signupFailed, description: msg });
      toast({ title: authLabels.signupFailed, description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setAuthNotice(null);

    if (!email) {
      setAuthNotice({
        variant: "destructive",
        title: authLabels.emailRequired,
        description: authLabels.emailRequiredDesc,
      });
      toast({ title: authLabels.emailRequired, description: authLabels.emailRequiredDesc });
      return;
    }
    
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth`,
      });
      if (error) throw error;
      setAuthNotice({ variant: "default", title: authLabels.emailSent, description: authLabels.emailSentDesc });
      toast({ title: authLabels.emailSent, description: authLabels.emailSentDesc });
    } catch (err) {
      const msg = err instanceof Error ? err.message : authLabels.resetFailed;
      setAuthNotice({ variant: "destructive", title: authLabels.resetFailed, description: msg });
      toast({ title: authLabels.resetFailed, description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!email) {
      toast({
        title: authLabels.emailRequired,
        description: authLabels.emailRequiredDesc,
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);
    try {
      const redirectUrl = `${window.location.origin}${COUNTRY_ROUTES.home(country)}`;
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: { emailRedirectTo: redirectUrl }
      });
      
      if (error) throw error;
      toast({
        title: authLabels.emailSent,
        description: authLabels.emailSentDesc,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : authLabels.resetFailed;
      toast({ title: authLabels.resetFailed, description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  async function startCheckout() {
    if (checkoutLoading) return; // Prevent double-clicks
    
    setCheckoutLoading(true);
    try {
      // First verify we still have a valid session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        toast({ 
          title: 'Sesiunea a expirat', 
          description: 'Te rugăm să te autentifici din nou pentru a continua.', 
          variant: "destructive" 
        });
        setCheckoutLoading(false);
        // Redirect to login after a short delay
        setTimeout(() => {
          window.location.href = `/auth?redirect=${encodeURIComponent(window.location.pathname)}`;
        }, 1500);
        return;
      }
      
      // Fire Meta InitiateCheckout pixel before Stripe redirect
      trackInitiateCheckout();
      
      toast({ title: t('toast_creating'), description: t('toast_redirecting') });
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: priceId ? { priceId } : {},
      });
      
      if (error) {
        // Check if it's an authentication error (401)
        const errorMessage = error.message?.toLowerCase() || '';
        const isAuthError = error.status === 401 || 
                           errorMessage.includes('unauthorized') || 
                           errorMessage.includes('not authenticated') ||
                           errorMessage.includes('authentication');
        
        if (isAuthError) {
          toast({ 
            title: 'Sesiunea a expirat', 
            description: 'Te rugăm să te autentifici din nou pentru a continua.', 
            variant: "destructive" 
          });
          setCheckoutLoading(false);
          setTimeout(() => {
            window.location.href = `/auth?redirect=${encodeURIComponent(window.location.pathname)}`;
          }, 1500);
          return;
        }
        
        setCheckoutLoading(false);
        toast({ 
          title: t('toast_error_title'), 
          description: t('toast_error_desc'), 
          variant: "destructive" 
        });
        return;
      }
      
      if (data?.url) {
        // Keep loading state - user is being redirected
        try {
          window.location.assign(data.url);
        } catch {
          window.open(data.url, '_self');
        }
      } else {
        setCheckoutLoading(false);
        toast({ 
          title: t('toast_error_title'), 
          description: t('toast_error_no_url'), 
          variant: "destructive" 
        });
      }
    } catch {
      setCheckoutLoading(false);
      toast({ 
        title: t('toast_unexpected'), 
        description: t('toast_unexpected_desc'), 
        variant: "destructive" 
      });
    }
  }

  return (
    <section className="container mx-auto px-4 py-8 min-h-screen max-w-7xl">
      <SEOHead
        title="Prețuri Abonamente Dealeri Auto — de la 29,99 €/lună | RO Auto Market"
        description="Pachete dealer pe RO Auto Market: Start 29,99 €, Standard 50 €, Large 120 € pe lună. Import CSV automat, dashboard cu statistici, leaduri. Fără comision pe vânzări."
        canonicalPath={canonicalUrl.replace('https://roautomarket.ro', '')}
        structuredData={offerCatalogLd}
      />
      <SEOHreflang canonicalUrl={canonicalUrl} />
      
      <div className="surface-dark band-bleed -mt-8 mb-8 pt-3 pb-6">
      <Breadcrumbs items={[
        { label: 'Prețuri', href: '/pricing' }
      ]} />
      
      <div className="text-center">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          Prețuri pentru persoane fizice și dealeri
        </h1>
        <div className="mt-5 flex justify-center">
          <PaymentMethodsBadge variant="inline" />
        </div>
      </div>
      </div>

      <PricingSchema audience="all" pageUrl={canonicalUrl} />
      <PlansOverview />

      {/* Main content: Pricing + Auth side by side */}
      <div className="grid lg:grid-cols-2 gap-8 mb-12">
        {/* Auth Card - Prima coloană */}
        <div>
          {isAuthed ? (
            <Card className="p-6">
            <div className="text-center py-8">
                <div className="text-4xl mb-4">{hasCard === false ? '💳' : '✅'}</div>
                <h3 className="text-xl font-semibold mb-2">
                  {hasCard === false ? 'Înregistrează cardul' : 'Ești autentificat'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {hasCard === false 
                    ? "Pentru a activa abonamentul, înregistrează un card bancar. Plata de 50€ se procesează imediat după validarea cardului."
                    : !hasListings 
                      ? "Cardul e salvat. Poți publica primul anunț și activa abonamentul (50€/lună, taxare imediată)."
                      : "Poți activa abonamentul acum sau poți accesa dashboard-ul."
                  }
                </p>
                <div className="flex flex-col gap-3">
                  {hasCard === false ? (
                    // No card - force card setup
                    <Button 
                      className="w-full"
                      disabled={loading}
                      onClick={async () => {
                        setLoading(true);
                        try {
                          const { data: setupData, error: setupError } = await supabase.functions.invoke('setup-card');
                          if (!setupError && setupData?.url) {
                            window.location.href = setupData.url;
                            return;
                          }
                          toast({
                            title: "Eroare",
                            description: "Nu am putut deschide pagina Stripe. Încearcă din nou.",
                            variant: "destructive",
                          });
                        } catch (e) {
                          toast({
                            title: "Eroare de rețea",
                            description: "Verifică conexiunea și încearcă din nou.",
                            variant: "destructive",
                          });
                        } finally {
                          setLoading(false);
                        }
                      }}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Se procesează...
                        </>
                      ) : '💳 Înregistrează cardul acum'}
                    </Button>
                  ) : !hasListings ? (
                    // Has card, no listings - guide to create listing
                    <Button asChild className="w-full">
                      <Link to={COUNTRY_ROUTES.sell(country)}>
                        🚀 Publică primul anunț
                      </Link>
                    </Button>
                  ) : (
                    // User with listings - show checkout
                    <Button onClick={startCheckout} disabled={checkoutLoading} className="w-full">
                      {checkoutLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Se procesează...
                        </>
                      ) : `${t('activate_subscription')} - ${price}${t('per_month')}`
                      }
                    </Button>
                  )}
                  {hasCard !== false && (
                    <Button variant="outline" asChild className="w-full">
                      <Link to={COUNTRY_ROUTES.dashboard(country)}>Mergi la Dashboard</Link>
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ) : (
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4 text-center">{authLabels.authTitle}</h2>
              <Tabs value={activeAuthTab} onValueChange={setActiveAuthTab}>
                <TabsList className="grid grid-cols-2 w-full mb-4">
                  <TabsTrigger value="signup">{authLabels.signupTab}</TabsTrigger>
                  <TabsTrigger value="login">{authLabels.loginTab}</TabsTrigger>
                </TabsList>

                {authNotice && (
                  <Alert
                    variant={authNotice.variant === "destructive" ? "destructive" : "default"}
                    className="mb-4"
                  >
                    <AlertTitle>{authNotice.title}</AlertTitle>
                    {authNotice.description && (
                      <AlertDescription>{authNotice.description}</AlertDescription>
                    )}
                    {cardSetupPending && (
                      <Button
                        className="mt-3 w-full"
                        onClick={async () => {
                          setCardSetupPending(false);
                          setAuthNotice(null);
                          setLoading(true);
                          try {
                            const { data: setupData, error: setupError } = await supabase.functions.invoke('setup-card');
                            if (!setupError && setupData?.url) {
                              window.location.href = setupData.url;
                              return;
                            }
                            setAuthNotice({
                              variant: "destructive",
                              title: "⚠️ Eroare la înregistrarea cardului",
                              description: "Nu am putut deschide pagina Stripe. Contactează-ne pe WhatsApp.",
                            });
                            setCardSetupPending(true);
                          } catch (e) {
                            setAuthNotice({
                              variant: "destructive",
                              title: "⚠️ Eroare la înregistrarea cardului",
                              description: "Eroare de rețea. Verifică conexiunea și încearcă din nou.",
                            });
                            setCardSetupPending(true);
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={loading}
                      >
                        {loading ? 'Se procesează...' : '💳 Înregistrează cardul acum'}
                      </Button>
                    )}
                  </Alert>
                )}
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-base font-medium">{authLabels.emailLabel}</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input 
                          id="email" 
                          type="email" 
                          placeholder={authLabels.emailPlaceholder}
                          value={email} 
                          onChange={(e) => setEmail(e.target.value)} 
                          className="pl-10 h-12"
                          required 
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-base font-medium">{authLabels.passwordLabel}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input 
                          id="password" 
                          type={showPassword ? "text" : "password"}
                          placeholder={authLabels.passwordPlaceholder}
                          value={password} 
                          onChange={(e) => setPassword(e.target.value)} 
                          className="pl-10 pr-10 h-12"
                          required 
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="remember" 
                          checked={rememberMe}
                          onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                        />
                        <label htmlFor="remember" className="text-sm cursor-pointer">
                          {authLabels.rememberMe}
                        </label>
                      </div>
                      <Button 
                        type="button" 
                        variant="link" 
                        onClick={handlePasswordReset}
                        className="p-0 h-auto text-primary"
                      >
                        {authLabels.forgotPassword}
                      </Button>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loading} 
                      className="w-full h-12 text-base font-semibold"
                    >
                      {loading ? authLabels.loading : authLabels.signInButton}
                    </Button>

                    <div className="text-center text-sm">
                      <span className="text-muted-foreground">{authLabels.noAccount}</span>
                      <Button 
                        type="button"
                        variant="link" 
                        onClick={() => {
                          const signupTab = document.querySelector('[value="signup"]') as HTMLElement;
                          signupTab?.click();
                        }}
                        className="p-0 h-auto text-primary font-medium"
                      >
                        {authLabels.signUpButton}
                      </Button>
                    </div>
                  </form>
                </TabsContent>
                
                <TabsContent value="signup">
                  <form onSubmit={handleSignup} className="space-y-3" noValidate>
                    {/* Account type selector */}
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <button type="button" onClick={() => setAccountType("buyer")}
                        className={`flex items-center justify-center gap-2 rounded-lg border-2 p-3 text-sm font-medium transition-colors ${accountType === "buyer" ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-muted-foreground"}`}>
                        <User className="h-4 w-4" /> Cumpărător
                      </button>
                      <button type="button" onClick={() => navigate("/dealeri?plan=standard")}
                        className="flex items-center justify-center gap-2 rounded-lg border-2 border-border hover:border-muted-foreground p-3 text-sm font-medium transition-colors">
                        <Building2 className="h-4 w-4" /> Dealer
                      </button>
                    </div>

                    {/* Name */}
                    <div className="space-y-1.5">
                      <Label htmlFor="signup-name">Nume complet</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input id="signup-name" placeholder="Ion Popescu" value={name}
                          onChange={(e) => { setName(e.target.value); clearSignupError('name'); }} className={`pl-10 h-11 ${signupErrors.name ? 'border-destructive' : ''}`} />
                      </div>
                      {signupErrors.name && <p className="text-sm text-destructive">{signupErrors.name}</p>}
                    </div>

                    {/* Phone */}
                    <div className="space-y-1.5">
                      <Label htmlFor="signup-phone">Telefon</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input id="signup-phone" type="tel" placeholder="+40 7XX XXX XXX" value={phone}
                          onChange={(e) => { setPhone(e.target.value); clearSignupError('phone'); }} className={`pl-10 h-11 ${signupErrors.phone ? 'border-destructive' : ''}`} />
                      </div>
                      {signupErrors.phone && <p className="text-sm text-destructive">{signupErrors.phone}</p>}
                    </div>

                    {/* Email */}
                    <div className="space-y-1.5">
                      <Label htmlFor="email2">{authLabels.emailLabel}</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input id="email2" type="email" placeholder={authLabels.emailPlaceholder}
                          value={email} onChange={(e) => { setEmail(e.target.value); clearSignupError('email'); }} className={`pl-10 h-11 ${signupErrors.email ? 'border-destructive' : ''}`} />
                      </div>
                      {signupErrors.email && <p className="text-sm text-destructive">{signupErrors.email}</p>}
                    </div>
                    
                    {/* Password */}
                    <div className="space-y-1.5">
                      <Label htmlFor="password2">{authLabels.passwordLabel}</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input id="password2" type={showPassword ? "text" : "password"}
                          placeholder={authLabels.passwordPlaceholder} value={password}
                          onChange={(e) => { setPassword(e.target.value); clearSignupError('password'); }} className={`pl-10 pr-10 h-11 ${signupErrors.password ? 'border-destructive' : ''}`} />
                        <button type="button" onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className={`text-sm ${password && password.length >= 8 ? 'text-primary' : 'text-muted-foreground'}`}>
                        {password && password.length >= 8 ? '✓ Parolă validă' : 'Minim 8 caractere'}
                      </p>
                      {signupErrors.password && <p className="text-sm text-destructive">{signupErrors.password}</p>}
                    </div>

                    {/* Dealer accounts use the full /dealeri wizard (verificare firmă, plan, card) */}
                    {accountType === "dealer" && (
                      <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-4 space-y-3">
                        <p className="text-sm font-medium text-foreground">
                          Contul de dealer se creează prin formularul complet de pe pagina dedicată (verificare firmă + selectare plan + plată card).
                        </p>
                        <Button type="button" onClick={() => navigate("/dealeri?plan=standard")} className="w-full h-11">
                          Continuă la înregistrarea dealer →
                        </Button>
                      </div>
                    )}

                    {/* GDPR Consent Section - similar to OLX/Autovit style */}
                    <div className="border-t pt-4 mt-2">
                      <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
                        {/* Paragraph 1: Terms acceptance (implicit) */}
                        <p>
                          {authLabels.consentIntro}{' '}
                          <a href={country === 'RO' ? '/termeni-si-conditii' : country === 'DE' ? '/de/terms' : '/uk/terms'} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="text-primary hover:underline font-medium"
                          >
                            {authLabels.termsOfUse}
                          </a>.
                        </p>
                        
                        {/* Paragraph 2: Data processing info */}
                        <p>
                          {authLabels.consentDataProcessing}{' '}
                          <a href={country === 'RO' ? '/politica-de-confidentialitate' : country === 'DE' ? '/de/privacy' : '/uk/privacy'} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="text-primary hover:underline font-medium"
                          >
                            {authLabels.privacyPolicy}
                          </a>{' '}
                          {authLabels.andThe}{' '}
                          <a href={country === 'RO' ? '/politica-cookies' : country === 'DE' ? '/de/privacy#cookies' : '/uk/privacy#cookies'} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="text-primary hover:underline font-medium"
                          >
                            {authLabels.cookiePolicy}
                          </a>.{' '}
                          {authLabels.consentDataUsage}
                        </p>
                        
                        {/* Checkbox: Marketing consent (optional) */}
                        <div className="flex items-start space-x-3">
                          <Checkbox 
                            id="marketingConsent" 
                            checked={marketingConsent}
                            onCheckedChange={(checked) => setMarketingConsent(checked === true)}
                            className="mt-0.5"
                          />
                          <label htmlFor="marketingConsent" className="cursor-pointer leading-relaxed">
                            {authLabels.consentMarketing}
                          </label>
                        </div>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loading} 
                      className="w-full h-12 text-base font-semibold"
                    >
                      {loading ? authLabels.loading : authLabels.signUpButton}
                    </Button>

                    <div className="text-center text-sm">
                      <span className="text-muted-foreground">{authLabels.hasAccount}</span>
                      <Button 
                        type="button"
                        variant="link" 
                        onClick={() => {
                          const loginTab = document.querySelector('[value="login"]') as HTMLElement;
                          loginTab?.click();
                        }}
                        className="p-0 h-auto text-primary font-medium"
                      >
                        {authLabels.signInButton}
                      </Button>
                    </div>
                    
                    <div className="text-center text-sm border-t pt-4 mt-4">
                      <span className="text-muted-foreground">{authLabels.noConfirmationEmail}</span>
                      <Button 
                        type="button"
                        variant="link" 
                        onClick={handleResendConfirmation}
                        className="p-0 h-auto text-primary font-medium block mx-auto"
                      >
                        {authLabels.resendEmail}
                      </Button>
                    </div>
                  </form>
                </TabsContent>
              </Tabs>
            </Card>
          )}
        </div>

        {/* Pricing Card - A doua coloană */}
        <div className="space-y-6">
          <Card className="border-2 border-primary">
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-1 rounded-t-lg">
              <div className="bg-orange-100 text-orange-800 text-xs font-bold px-3 py-1 rounded-t text-center">
                {t('limited_offer')}
              </div>
            </div>
            <div className="p-6">
              <div className="text-xl font-semibold mb-1">{t('dealer_basic')}</div>
              <div className="text-3xl font-bold mb-4">
                <span className="text-primary">{price} {t('per_month')}</span>
                <span className="text-sm font-medium text-muted-foreground block">{t('immediate_payment')}</span>
              </div>
              <ul className="list-disc ml-5 text-sm text-muted-foreground mb-4 space-y-1">
                {country === 'UK' ? (
                  <>
                    <li>Full stock imported by us within 24 hours of account creation.</li>
                    <li>You appear in front of active buyers searching for cars right now.</li>
                    <li>Real-time notifications when a buyer calls, sends WhatsApp, messages, requests an offer or reserves a car.</li>
                    <li>Performance report every 3 days with views and contacts for each car.</li>
                    <li>The Verified Dealer badge is awarded after verification, regardless of plan.</li>
                    <li>Automatic 48-hour visibility boost for every new car added.</li>
                    <li><strong>0% sales commission.</strong> You only pay for presence, not for success.</li>
                    <li>Full control over your stock from the dashboard — add, edit or delete any car anytime.</li>
                  </>
                ) : country === 'DE' ? (
                  <>
                    <li>Kompletter Bestand von uns innerhalb von 24 Stunden nach Kontoerstellung importiert.</li>
                    <li>Sie erscheinen vor aktiven Käufern, die gerade jetzt nach Autos suchen.</li>
                    <li>Echtzeit-Benachrichtigungen bei Anruf, WhatsApp, Nachricht, Angebotsanfrage oder Reservierung.</li>
                    <li>Leistungsbericht alle 3 Tage mit Aufrufen und Kontakten pro Fahrzeug.</li>
                    <li>Abzeichen <strong>Geprüfter Händler</strong> auf jedem Inserat in Ihrem Bestand sichtbar.</li>
                    <li>Automatischer 48-Stunden-Sichtbarkeitsboost für jedes neu hinzugefügte Fahrzeug.</li>
                    <li><strong>0% Verkaufsprovision.</strong> Sie zahlen nur für Präsenz, nicht für Erfolg.</li>
                    <li>Volle Kontrolle über Ihren Bestand im Dashboard — jedes Auto jederzeit hinzufügen, bearbeiten oder löschen.</li>
                  </>
                ) : (
                  <>
                    <li>Poți importa stocul prin CSV sau XML.</li>
                    <li>Apari în fața cumpărătorilor activi care caută mașini în acest moment.</li>
                    <li>Notificări în timp real când un cumpărător te sună, trimite WhatsApp, mesaj, cere ofertă sau rezervă o mașină.</li>
                    <li>Ai acces la statistici despre anunțuri în cont.</li>
                    <li><strong>0% comision la vânzare.</strong> Plătești doar prezența, nu și succesul.</li>
                    <li>Control complet asupra stocului din dashboard, adaugi, editezi sau ștergi orice mașină oricând.</li>
                  </>
                )}
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs">
                <p className="font-semibold text-blue-900 mb-1">{t('vat_info_title')}</p>
                <p className="text-blue-800" dangerouslySetInnerHTML={{ __html: t('vat_info_desc') }} />
              </div>
              
              {isAuthed ? (
                !hasListings ? (
                  // New user without listings
                  <Button asChild className="w-full">
                    <Link to={COUNTRY_ROUTES.sell(country)}>
                      Publică primul anunț
                    </Link>
                  </Button>
                ) : (
                  // User with listings
                  <Button className="w-full" onClick={startCheckout} disabled={checkoutLoading}>
                    {checkoutLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Se procesează...
                      </>
                    ) : `${t('activate_subscription')} - ${price}${t('per_month')}`
                    }
                  </Button>
                )
              ) : (
                <Button asChild className="w-full">
                  <Link to="/dealeri?plan=standard">Creează cont dealer</Link>
                </Button>
              )}
            </div>
          </Card>

        </div>

      </div>

      {/* Additional SEO Content */}
      <div className="max-w-4xl mx-auto mt-12 prose prose-gray">
        <h2 className="text-2xl font-semibold mb-4">{t('seo_title')}</h2>
        
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-xl font-semibold mb-3">Pentru dealeri</h3>
            <p className="text-sm">Publici anunțurile dintr-un singur cont. Poți importa stocul prin CSV sau XML. Vezi contactele și statisticile în cont.</p>
          </div>
          
        </div>




        <h3 className="text-xl font-semibold mb-3">{t('faq_title')}</h3>
        
        <div className="space-y-4 mb-8">
          <div>
            <h4 className="font-semibold">{t('faq_cancel_q')}</h4>
            <p className="text-muted-foreground">{t('faq_cancel_a')}</p>
          </div>
          
          <div>
            <h4 className="font-semibold">{t('faq_listings_q')}</h4>
            <p className="text-muted-foreground">{t('faq_listings_a')}</p>
          </div>
          
          <div>
            <h4 className="font-semibold">{t('faq_invoices_q')}</h4>
            <p className="text-muted-foreground">{t('faq_invoices_a')}</p>
          </div>
        </div>


        <div className="text-center">
          <h3 className="text-xl font-semibold mb-4">{t('ready_title')}</h3>
          <p className="text-muted-foreground mb-6">
            {t('ready_desc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={getLink('/cum-functioneaza')} className="text-primary hover:underline">
              {t('ready_see_how')}
            </Link>
            <Link to={getLink('/contact')} className="text-primary hover:underline">
              {t('ready_contact')}
            </Link>
          </div>
        </div>

        <div className="text-center mt-10 pt-6 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Ești persoană fizică și vrei să vinzi mașina ta?{" "}
            <Link to="/vinde-masina" className="text-primary hover:underline font-medium">
              Vinde mașina ta →
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}
